import { type LoyaltyUser } from '@koala/sdk';
import Link from 'next/link';
import { type ConnectedProps, connect } from 'react-redux';
import { PhoneCaptureForm } from './phone-capture-form';
import { StyledUserInfo, StyledEmailVerification, StyledPhoneDisclosure } from './styles';
import { genericEventHandler } from '@/analytics/events';
import { GlobalEvents } from '@/analytics/events/constants';
import { LinkButton } from '@/components/checkout/uielements';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledFormField, StyledGenericForm } from '@/components/uielements/form-fields/styles';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { CHECKOUT_AUTH_TYPES } from '@/constants/checkout';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import { ROUTES } from '@/constants/routes';
import { type RootState } from '@/types/app';
import { type IUserInfo } from '@/types/checkout';
import { isUser, phoneNumberFormatter } from '@/utils/auth';

interface Props extends ReduxProps {
  toggleDisplayState: (state: string) => void;
}

const _LoginButtons = ({ meLoading, toggleDisplayState }: Props) => (
  <StyledGenericForm as="div">
    <StyledFormField>
      <StyledPrimaryButton
        data-css-override="StyledPrimaryButton"
        data-testid="signin-button"
        style={{ width: '100%' }}
        $buttonTheme="primary"
        type="submit"
        $submitting={meLoading}
        onClick={() => {
          toggleDisplayState(CHECKOUT_AUTH_TYPES.LOGIN);
          genericEventHandler(GlobalEvents.AUTH__CLICK_SIGN_IN);
        }}
      >
        {meLoading ? 'LOADING' : <StringAccessor accessor="login.cta" />}
      </StyledPrimaryButton>
    </StyledFormField>
    <StyledFormField>
      <StyledPrimaryButton
        data-css-override="StyledPrimaryButton"
        data-testid="signup-button"
        style={{ width: '100%' }}
        $buttonTheme="knockout"
        type="submit"
        onClick={() => {
          toggleDisplayState(CHECKOUT_AUTH_TYPES.SIGNUP);
          genericEventHandler(GlobalEvents.AUTH__CLICK_SIGN_UP);
        }}
      >
        <StringAccessor accessor="signup.cta" />
      </StyledPrimaryButton>
    </StyledFormField>
    <LinkButton
      data-testid="checkout-as-guest-button"
      className={CSS_CLASSES.AUTH.CHECKOUT_AS_GUEST_LINK}
      style={{ marginLeft: 'auto', marginRight: 'auto' }}
      onClick={() => {
        toggleDisplayState(CHECKOUT_AUTH_TYPES.GUEST);
        genericEventHandler(GlobalEvents.AUTH__CLICK_GUEST_CHECKOUT);
      }}
    >
      <StringAccessor html={true} accessor="cart_checkout.guest_checkout_cta" />
    </LinkButton>
  </StyledGenericForm>
);

const mapStateToProps = (state: RootState) => ({
  meLoading: state.app.me.loading,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export const LoginButtons = connector(_LoginButtons);

interface UserInfoProps {
  appendUserPhone: (phone: string) => void;
  user: LoyaltyUser | IUserInfo;
  onEdit: () => void;
}
export const UserInfo = ({ appendUserPhone, onEdit, user }: UserInfoProps) => (
  <>
    {/* If phone exists, display it with name */}
    {user?.phone && (
      <StyledUserInfo data-testid="user-info-block">
        <p style={{ margin: 0 }}>
          {user.first_name} {user.last_name}
          <br />
          {user.email}
          <br />
          {phoneNumberFormatter(user.phone)}
        </p>
        {!isUser(user) && onEdit()}
      </StyledUserInfo>
    )}

    {/* If no phone exists on a registered user, we need to capture it */}
    <Modal.Container
      dismissible={false}
      requestVisibility={!!(isUser(user) && user.id && !user.phone)}
    >
      {(toggle) => (
        <>
          <Modal.Title>Please confirm your phone number:</Modal.Title>
          <Modal.Content>
            <PhoneCaptureForm
              onPhoneCapture={(phone) => {
                appendUserPhone(phone);
                toggle();
              }}
            />
          </Modal.Content>
        </>
      )}
    </Modal.Container>
  </>
);

export const ForgotPassword = () => (
  <Link href={ROUTES.FORGOT_PASSWORD} passHref={true} legacyBehavior>
    <LinkButton
      as="a"
      onClick={() => genericEventHandler(GlobalEvents.AUTH__CLICK_FORGOT_PASSWORD)}
    >
      <StringAccessor accessor="forgot_password.prompt" />
    </LinkButton>
  </Link>
);

interface HeaderButtonProps {
  accessor: string;
  onClick: () => void;
}
export const HeaderButton = ({ accessor, onClick }: HeaderButtonProps) => (
  <LinkButton onClick={onClick}>
    <StringAccessor accessor={accessor} />
  </LinkButton>
);

interface EmailVerificationProps {
  meLoading: boolean;
  toggleDisplayState: (state: string) => void;
  toggleEmailVerification: (show: boolean) => void;
}

export const EmailVerification = ({
  toggleDisplayState,
  toggleEmailVerification,
  meLoading,
}: EmailVerificationProps) => (
  <StyledEmailVerification>
    <StringAccessor accessor="cart_checkout.email_verification_text" tag="p" />

    <StyledGenericForm>
      <StyledFormField>
        <StyledPrimaryButton
          data-css-override="StyledPrimaryButton"
          style={{ width: '100%' }}
          $buttonTheme="primary"
          type="submit"
          $submitting={meLoading}
          onClick={() => {
            toggleDisplayState(CHECKOUT_AUTH_TYPES.LOGIN);
            genericEventHandler(GlobalEvents.AUTH__CLICK_SIGN_IN);
            toggleEmailVerification(false);
          }}
        >
          {meLoading ? 'LOADING' : <StringAccessor accessor="login.cta" />}
        </StyledPrimaryButton>
      </StyledFormField>

      <StyledFormField $autoHeight={true}>
        <StyledPrimaryButton
          data-css-override="StyledPrimaryButton"
          style={{ width: '100%' }}
          $buttonTheme="knockout"
          type="submit"
          onClick={() => {
            toggleDisplayState(CHECKOUT_AUTH_TYPES.GUEST);
            genericEventHandler(GlobalEvents.AUTH__CLICK_GUEST_CHECKOUT);
            toggleEmailVerification(false);
          }}
        >
          <StringAccessor accessor="cart_checkout.guest_checkout_continue_cta" />
        </StyledPrimaryButton>
      </StyledFormField>
    </StyledGenericForm>
  </StyledEmailVerification>
);

export const PhoneNumberDisclosure = ({ brandName }: { brandName: string }) => (
  <StyledPhoneDisclosure>
    By providing your phone number, you consent to receive text messages from {brandName} for order
    updates & loyalty programs. Standard message rates may apply. See our{' '}
    <a
      target="_blank"
      href="https://koala-configurations.s3.us-east-1.amazonaws.com/public/assets/Chowly-Terms-and-Conditions-b3cca027-acb6-42db-9d1f-11080e29b1e1.pdf"
      rel="noreferrer"
    >
      Terms and Conditions
    </a>
    .
  </StyledPhoneDisclosure>
);
